import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { T } from "../themes";
import { PERSONA_PREVIEW, SIMPLE_GOAL_PRESETS } from "../constants";
import { Lightning, SignOut, WarningCircle, Trash, Bell, BellSlash, GearSix, User, ShieldCheck, LinkSimple, CaretRight, DownloadSimple, PencilSimple, Heart, ChatCentered } from "@phosphor-icons/react";
import Btn from "../components/Btn";
import Field from "../components/Field";
import Spinner from "../components/Spinner";
import GenderToggle from "../components/GenderToggle";
import PersonaPicker from "../components/PersonaPicker";
import SyncBadge from "../components/SyncBadge";
import AppHeader from "../components/AppHeader";
import BottomNav from "../components/BottomNav";

// ── Tab bar ────────────────────────────────────────────────────────────────────
const TABS = ["Settings", "Profile", "Account", "Notifs", "Connected"];

function TabBar({ active, setActive }) {
  return (
    <div style={{
      background: T.card, margin: "0 16px 12px", borderRadius: T.radiusSm,
      border: `1px solid ${T.cardBorder}`, padding: 4,
      display: "flex", gap: 2, overflowX: "auto",
    }}>
      {TABS.map((label, i) => (
        <button
          key={label}
          onClick={() => setActive(i)}
          style={{
            background: active === i ? T.orange : "transparent",
            color: active === i ? "#fff" : T.inkMuted,
            borderRadius: T.radiusSm, padding: "8px 14px",
            fontSize: 11, fontWeight: 700, fontFamily: T.fontSans,
            whiteSpace: "nowrap", border: "none", cursor: "pointer",
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// ── Nudge toggle row ───────────────────────────────────────────────────────────
function NudgeToggle({ id, label, description }) {
  const storageKey = `nudge_pref_${id}`;
  const [enabled, setEnabled] = useState(() => {
    try { const v = localStorage.getItem(storageKey); return v === null ? true : v !== "false"; }
    catch(_) { return true; }
  });
  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    try { localStorage.setItem(storageKey, String(next)); } catch(_) {}
  };
  return (
    <div style={{
      background: T.card, borderRadius: T.radius, border: `1px solid ${T.cardBorder}`,
      padding: "14px 16px", marginBottom: 8,
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
    }}>
      <div style={{ flex: 1 }}>
        <p style={{ margin: 0, fontSize: 13, fontFamily: T.fontSans, fontWeight: 600, color: T.inkDark }}>{label}</p>
        <p style={{ margin: "2px 0 0", fontSize: 11, fontFamily: T.fontSans, color: T.inkMuted, lineHeight: 1.4 }}>
          {description}
        </p>
      </div>
      <div
        onClick={toggle}
        style={{
          width: 44, height: 24, borderRadius: 12, cursor: "pointer",
          background: enabled ? T.primary : T.surfaceLight,
          position: "relative", transition: "background 0.2s", flexShrink: 0,
        }}
      >
        <div style={{
          width: 20, height: 20, borderRadius: T.radiusSm, background: "#fff",
          position: "absolute", top: 2,
          left: enabled ? 22 : 2,
          transition: "left 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }}/>
      </div>
    </div>
  );
}

// SIMPLE_GOAL_PRESETS imported from ../constants — single source of truth

export default function SettingsScreen({
  protocolModal,
  shell,
  phone,
  OfflineBanner,
  hasTargets,
  setScreen,
  screen,
  session,
  syncing,
  syncErr,
  height,
  setHeight,
  weight,
  setWeight,
  age,
  setAge,
  gender,
  setGender,
  bmrPreview,
  targets,
  goal,
  setGoal,
  physiqueGoal,
  setPhysiqueGoal,
  wakeTime,
  setWakeTime,
  bedTime,
  setBedTime,
  autoAuditTime,
  setAutoAuditTime,
  saveProfile,
  darkMode,
  setDarkMode,
  persona,
  setPersona,
  country,
  setCountry,
  onboardErr,
  isGenerating,
  generateTargets,
  handleLogout,
  exportLoading,
  handleExportData,
  exportErr,
  showDeleteModal,
  setShowDeleteModal,
  deleteConfirmText,
  setDeleteConfirmText,
  deleteErr,
  setDeleteErr,
  deleteLoading,
  handleDeleteAccount,
  targetWeight,
  setTargetWeight,
  goalMode,
  setGoalMode,
  simpleGoal,
  setSimpleGoal,
  isPremium,
  setShowPaywall,
  setPaywallReason,
  healthConnectAvailable,
  healthConnectGranted,
  onConnectHealthConnect,
}) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [hcWebMsg, setHcWebMsg] = useState(false);
  const [hcNativeErr, setHcNativeErr] = useState(false);
  const [hcSuccess, setHcSuccess] = useState(false);

  // ── Tab 0: SETTINGS ─────────────────────────────────────────────────────────
  const tabSettings = (
    <div style={{ padding: "16px 16px 48px" }}>

      {/* Coach Persona */}
      <div style={{ fontSize: 10, fontWeight: 700, color: T.inkMuted, letterSpacing: "1px", margin: "0 16px", marginBottom: 6 }}>Coach persona</div>
      <div style={{ background: T.card, borderRadius: 18, border: `1px solid ${T.cardBorder}`, padding: 16, marginBottom: 8 }}>
        <PersonaPicker value={persona} isPremium={isPremium}
          onChange={async (p) => { setPersona(p); await saveProfile({ persona: p }); }}
          onLocked={() => { setPaywallReason("persona"); setShowPaywall(true); }}/>
      </div>

      {/* Psych-Mode indicator */}
      <div style={{
        background: T.tileBg, borderRadius: T.radiusSm, border: `1px solid ${T.cardBorder}`,
        padding: "12px 16px", marginBottom: 8,
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <div style={{ display: "flex", gap: 5 }}>
          {(({
            Balanced:     ["#1a6b8a","#2196b8","#e6a817","#ffffff"],
            Motivational: ["#4a9e2e","#7ec850","#f9c74f","#ffffff"],
            Critical:     ["#a32d2d","#e24b4a","#e8a020","#1c1c1e"],
          })[persona] || [T.accent, T.secondary, T.yellow, T.bg]).map((c, i) => (
            <div key={i} style={{ width: 20, height: 20, background: c, borderRadius: 6 }}/>
          ))}
        </div>
        <div>
          <p style={{ color: T.tileAccent, fontSize: 11, fontFamily: T.fontDisplay, fontWeight: 700, margin: 0 }}>
            Psych-Mode: {persona}
          </p>
          <p style={{ color: T.tileMuted, fontSize: 10, fontFamily: T.fontSans, margin: 0 }}>
            {PERSONA_PREVIEW[persona]?.label}
          </p>
        </div>
      </div>

      {/* Display */}
      <div style={{ fontSize: 10, fontWeight: 700, color: T.inkMuted, letterSpacing: "1px", margin: "12px 16px 6px" }}>Display</div>
      <div style={{
        background: T.card, borderRadius: T.radius, border: `1px solid ${T.cardBorder}`,
        padding: "14px 16px", marginBottom: 8,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <p style={{ margin: 0, fontSize: 13, fontFamily: T.fontSans, fontWeight: 600, color: T.inkDark }}>
            Dark mode
          </p>
          <p style={{ margin: "2px 0 0", fontSize: 11, fontFamily: T.fontSans, color: T.inkMuted }}>
            Easier on the eyes at the gym
          </p>
        </div>
        <div
          onClick={() => setDarkMode(d => !d)}
          style={{
            width: 44, height: 24, borderRadius: 12, cursor: "pointer",
            background: darkMode ? T.primary : T.surfaceLight,
            position: "relative", transition: "background 0.2s", flexShrink: 0,
          }}
        >
          <div style={{
            width: 20, height: 20, borderRadius: T.radiusSm, background: "#fff",
            position: "absolute", top: 2,
            left: darkMode ? 22 : 2,
            transition: "left 0.2s",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }}/>
        </div>
      </div>

      {/* Daily Schedule */}
      <div style={{ fontSize: 10, fontWeight: 700, color: T.inkMuted, letterSpacing: "1px", margin: "12px 16px 6px" }}>Daily schedule</div>
      <div style={{ background: T.card, borderRadius: T.radius, border: `1px solid ${T.cardBorder}`, padding: 16, marginBottom: 8 }}>
        <p style={{ color: T.inkMuted, fontSize: 11, fontFamily: T.fontSans, margin: "0 0 10px", lineHeight: 1.5 }}>
          Used by the AI coach to calibrate pacing commands to your actual day.
        </p>
        <div style={{ display: "flex", gap: 9 }}>
          <div style={{ flex: 1 }}>
            <Field label="Wake Up" type="time" value={wakeTime} onChange={e => setWakeTime(e.target.value)}/>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Bedtime" type="time" value={bedTime} onChange={e => setBedTime(e.target.value)}/>
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          <label style={{
            color: T.inkMuted, fontSize: 10, fontFamily: T.fontSans, fontWeight: 700,
            letterSpacing: "1px", display: "block", marginBottom: 4,
          }}>
            Auto-audit time
            <span style={{
              color: T.inkMuted, fontSize: 10, fontFamily: T.fontSans,
              fontWeight: 500, marginLeft: 6, textTransform: "none", letterSpacing: 0,
            }}>
              (leave blank to disable)
            </span>
          </label>
          <input
            type="time" value={autoAuditTime}
            onChange={e => setAutoAuditTime(e.target.value)}
            style={{
              width: "100%", background: T.surfaceLight, border: `1px solid ${T.cardBorder}`,
              borderRadius: 12, padding: "11px 14px", color: T.inkDark,
              fontSize: 14, fontFamily: T.fontSans,
              outline: "none", boxSizing: "border-box",
            }}
          />
          {autoAuditTime && (
            <p style={{ color: T.primary, fontSize: 11, fontFamily: T.fontSans, fontWeight: 500, margin: "4px 0 0" }}>
              Audit will run automatically at {autoAuditTime} daily
            </p>
          )}
        </div>
      </div>

      {/* Market Region */}
      <div style={{ fontSize: 10, fontWeight: 700, color: T.inkMuted, letterSpacing: "1px", margin: "12px 16px 6px" }}>Market region</div>
      <div style={{ background: T.card, borderRadius: T.radius, border: `1px solid ${T.cardBorder}`, padding: 16, marginBottom: 8 }}>
        <p style={{ color: T.inkMuted, fontSize: 11, fontFamily: T.fontSans, margin: "0 0 10px", lineHeight: 1.5 }}>
          Controls which country's food database is searched.
        </p>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[
            { id: "uk",    label: "UK" },
            { id: "us",    label: "USA" },
            { id: "au",    label: "Australia" },
            { id: "world", label: "World" },
          ].map(r => (
            <button
              key={r.id}
              onClick={() => setCountry(r.id)}
              style={{
                padding: "8px 14px", borderRadius: T.radiusSm,
                background: country === r.id ? T.orange : T.surfaceLight,
                border: `1px solid ${country === r.id ? T.orange : T.cardBorder}`,
                color: country === r.id ? "#fff" : T.inkMuted,
                fontSize: 12, fontWeight: 600, fontFamily: T.fontSans,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Save settings */}
      <div style={{ padding: "8px 0 0" }}>
        <Btn color={T.orange} onClick={async () => {
          await saveProfile({ persona, country, wakeTime, bedTime, autoAuditTime });
        }}>
          Save settings
        </Btn>
      </div>

    </div>
  );

  // ── Tab 1: PROFILE ──────────────────────────────────────────────────────────
  const tabProfile = (
    <div style={{ padding: "0 16px 24px" }}>

      {/* Avatar + name/email */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 0 14px" }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%", background: T.primary,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 24, fontWeight: 700, color: "#fff", lineHeight: 1 }}>
            {(session?.user?.email?.[0] || "U").toUpperCase()}
          </span>
        </div>
        <div>
          <p style={{ margin: 0, fontFamily: T.fontDisplay, fontSize: 20, fontWeight: 800, color: T.inkDark }}>
            {session?.user?.email?.split("@")[0] || "User"}
          </p>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: T.inkMuted, fontFamily: T.fontSans }}>
            {session?.user?.email || ""}
          </p>
        </div>
      </div>

      {/* Biometrics */}
      <div style={{ fontSize: 10, fontWeight: 700, color: T.inkMuted, letterSpacing: "1px", marginBottom: 6 }}>Biometrics</div>
      <div style={{ background: T.card, borderRadius: T.radius, border: `1px solid ${T.cardBorder}`, padding: 16, marginBottom: 8 }}>
        <div style={{ display: "flex", gap: 9, marginBottom: 9 }}>
          <div style={{ flex: 1 }}><Field label="Height (cm)" type="number" value={height} onChange={e => setHeight(e.target.value)} placeholder="175"/></div>
          <div style={{ flex: 1 }}><Field label="Weight (kg)" type="number" value={weight} onChange={e => setWeight(e.target.value)} placeholder="75"/></div>
        </div>
        <div style={{ display: "flex", gap: 9, marginBottom: 9 }}>
          <div style={{ flex: 1 }}><Field label="Age" type="number" value={age} onChange={e => setAge(e.target.value)} placeholder="28"/></div>
          <div style={{ flex: 1 }}><GenderToggle value={gender} onChange={setGender}/></div>
        </div>
        <div style={{ display: "flex", gap: 9, marginBottom: 4 }}>
          <div style={{ flex: 1 }}>
            <Field
              label="Target weight (kg)"
              type="number"
              value={targetWeight !== null ? String(targetWeight) : ""}
              onChange={e => setTargetWeight(e.target.value ? parseFloat(e.target.value) : null)}
              placeholder="e.g. 78"
            />
          </div>
          <div style={{ flex: 1 }}/>
        </div>
        <p style={{ color: T.inkMuted, fontSize: 11, fontFamily: T.fontSans, margin: "0 0 0", lineHeight: 1.5 }}>
          Used to guide AI coaching and calorie adjustments
        </p>
      </div>

      {/* BMR preview */}
      {bmrPreview > 0 && (
        <div style={{
          background: T.card, borderRadius: T.radius, border: `1px solid ${T.cardBorder}`,
          padding: "14px 16px", marginBottom: 8,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <p style={{ color: T.inkMuted, fontSize: 10, fontFamily: T.fontSans, letterSpacing: "1px", margin: 0, fontWeight: 700 }}>Basal metabolic rate</p>
            <p style={{ color: T.inkMuted, fontSize: 11, fontFamily: T.fontSans, margin: "2px 0 0" }}>Mifflin-St Jeor</p>
          </div>
          <div style={{ textAlign: "right" }}>
            <span style={{ color: T.primary, fontSize: 32, fontWeight: 800, fontFamily: T.fontDisplay }}>{bmrPreview}</span>
            <span style={{ color: T.inkMuted, fontSize: 12, fontFamily: T.fontSans, fontWeight: 600 }}> kcal</span>
          </div>
        </div>
      )}
      {targets.calories > 0 && (
        <div style={{
          background: T.card, borderRadius: T.radius, border: `1px solid ${T.cardBorder}`,
          padding: "14px 16px", marginBottom: 8,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <p style={{ color: T.inkMuted, fontSize: 10, fontFamily: T.fontSans, letterSpacing: "1px", margin: 0, fontWeight: 700 }}>Active calorie target</p>
            <p style={{ color: T.inkMuted, fontSize: 11, fontFamily: T.fontSans, margin: "2px 0 0" }}>From generated targets</p>
          </div>
          <div style={{ textAlign: "right" }}>
            <span style={{ color: T.primary, fontSize: 32, fontWeight: 800, fontFamily: T.fontDisplay }}>{targets.calories}</span>
            <span style={{ color: T.inkMuted, fontSize: 12, fontFamily: T.fontSans, fontWeight: 600 }}> kcal</span>
          </div>
        </div>
      )}

      {/* Goal mode toggle */}
      <div style={{ fontSize: 10, fontWeight: 700, color: T.inkMuted, letterSpacing: "1px", margin: "12px 0 6px" }}>Goal mode</div>
      <div style={{ background: T.card, borderRadius: T.radius, border: `1px solid ${T.cardBorder}`, padding: 16, marginBottom: 8 }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
          {[
            { id: "simple", label: "Simple" },
            { id: "ai",     label: "AI / Custom" },
          ].map(m => (
            <button
              key={m.id}
              onClick={() => setGoalMode(m.id)}
              style={{
                flex: 1, padding: "9px 10px", borderRadius: T.radiusSm,
                background: goalMode === m.id ? T.orange : T.surfaceLight,
                border: `1px solid ${goalMode === m.id ? T.orange : T.cardBorder}`,
                color: goalMode === m.id ? "#fff" : T.inkMuted,
                fontSize: 12, fontFamily: T.fontSans, fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {m.label}
            </button>
          ))}
        </div>

        {goalMode === "simple" ? (
          <>
            <p style={{ color: T.inkMuted, fontSize: 11, fontFamily: T.fontSans, margin: "0 0 10px", lineHeight: 1.5 }}>
              Pick your primary goal. The AI will calibrate your targets accordingly.
            </p>
            <div style={{ display: "flex", gap: 6 }}>
              {[
                { id: "lose",     label: "Lose fat" },
                { id: "maintain", label: "Maintain" },
                { id: "bulk",     label: "Build muscle" },
              ].map(opt => (
                <button
                  key={opt.id}
                  onClick={() => {
                    setSimpleGoal(opt.id);
                    setGoal(SIMPLE_GOAL_PRESETS[opt.id]);
                  }}
                  style={{
                    flex: 1, padding: "12px 6px", borderRadius: 12,
                    background: simpleGoal === opt.id ? T.orange : T.surfaceLight,
                    border: `1px solid ${simpleGoal === opt.id ? T.orange : T.cardBorder}`,
                    color: simpleGoal === opt.id ? "#fff" : T.inkMuted,
                    fontSize: 12, fontFamily: T.fontSans, fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.15s", textAlign: "center",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <p style={{ color: T.inkMuted, fontSize: 11, fontFamily: T.fontSans, margin: "0 0 9px", lineHeight: 1.5 }}>
              Describe your lifestyle and training. The AI uses this to classify activity and calibrate the deficit.
            </p>
            <Field
              textarea
              value={goal}
              onChange={e => setGoal(e.target.value)}
              placeholder="e.g. Office job, gym 4x/week. Want to drop body fat and look lean..."
            />
          </>
        )}
      </div>

      {/* Physique Goal */}
      <div style={{ fontSize: 10, fontWeight: 700, color: T.inkMuted, letterSpacing: "1px", margin: "12px 0 6px" }}>Physique &amp; training goal</div>
      <div style={{ background: T.card, borderRadius: T.radius, border: `1px solid ${T.cardBorder}`, padding: 16, marginBottom: 8 }}>
        <p style={{ color: T.inkMuted, fontSize: 11, fontFamily: T.fontSans, margin: "0 0 9px", lineHeight: 1.5 }}>
          Describe the exact physique you are building toward. The AI coach audits every session against this.
        </p>
        <Field
          textarea
          value={physiqueGoal}
          onChange={e => setPhysiqueGoal(e.target.value)}
          placeholder="e.g. lean and athletic, visible abs at 12% BF, broad shoulders..."
        />
      </div>

      {/* Error */}
      {onboardErr && (
        <div style={{
          background: T.surfaceLight, border: `1px solid ${T.red || T.danger || "#e24b4a"}`,
          padding: "10px 14px", marginBottom: 8, borderRadius: T.radiusSm,
          color: T.red || T.danger || "#e24b4a", fontSize: 12, fontFamily: T.fontSans, fontWeight: 600,
          display: "flex", gap: 8, alignItems: "flex-start",
        }}>
          <WarningCircle size={15} weight="fill" style={{ flexShrink: 0, marginTop: 1 }}/><span>{onboardErr}</span>
        </div>
      )}

      {/* Generate Targets CTA */}
      <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 4 }}>
        <Btn
          color={T.orange}
          disabled={isGenerating}
          onClick={async () => {
            const h = parseFloat(height), w = parseFloat(weight), a = parseInt(age);
            await saveProfile({ height: String(h || ""), weight: String(w || ""), age: String(a || ""), gender, goal, persona, country, physiqueGoal, wakeTime, bedTime, targetWeight });
            await generateTargets(true);
          }}
        >
          {isGenerating
            ? <><Spinner size={13}/> AI Sports Nutritionist calculating protocol...</>
            : <><Lightning size={15} weight="fill"/> {hasTargets ? "Recalculate targets (AI)" : "Generate my targets"}</>
          }
        </Btn>
        {hasTargets && !isGenerating && (
          <p style={{ margin: 0, fontSize: 11, fontFamily: T.fontSans, color: T.inkMuted, textAlign: "center", lineHeight: 1.5 }}>
            Changing persona or display settings does not affect your targets. Only tap above to regenerate.
          </p>
        )}
      </div>

    </div>
  );

  // ── Tab 2: ACCOUNT ──────────────────────────────────────────────────────────
  const tabAccount = (
    <div style={{ padding: "0 16px 24px" }}>

      {/* Sign out */}
      <div style={{ padding: "16px 0 12px" }}>
        <Btn outline color={T.inkDark} onClick={handleLogout}><SignOut size={14} weight="bold"/> Sign out</Btn>
      </div>

      {/* Your Data */}
      <div style={{ fontSize: 10, fontWeight: 700, color: T.inkMuted, letterSpacing: "1px", marginBottom: 6 }}>Your data</div>
      <div style={{ background: T.card, borderRadius: T.radius, border: `1px solid ${T.cardBorder}`, padding: 16, marginBottom: 8 }}>
        <p style={{ color: T.inkMuted, fontSize: 11, fontFamily: T.fontSans, margin: "0 0 12px", lineHeight: 1.5 }}>
          Download a full copy of your Lithe data — food logs, weight, body measurements, supplements, and workouts (GDPR Article 20).
        </p>
        <div style={{ display: "flex", gap: 8, marginBottom: exportErr ? 8 : 0 }}>
          <button
            onClick={() => handleExportData("json")}
            disabled={!!exportLoading}
            style={{
              flex: 1, background: T.surfaceLight, border: `1px solid ${T.cardBorder}`, padding: "10px 12px",
              cursor: exportLoading ? "not-allowed" : "pointer", color: T.inkDark,
              fontSize: 12, fontFamily: T.fontSans, fontWeight: 600,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              opacity: exportLoading ? 0.6 : 1, borderRadius: 12,
            }}
          >
            {exportLoading === "json" ? <><Spinner size={11}/> Exporting...</> : <><DownloadSimple size={14} weight="bold"/> JSON</>}
          </button>
          <button
            onClick={() => handleExportData("csv")}
            disabled={!!exportLoading}
            style={{
              flex: 1, background: T.surfaceLight, border: `1px solid ${T.cardBorder}`, padding: "10px 12px",
              cursor: exportLoading ? "not-allowed" : "pointer", color: T.inkDark,
              fontSize: 12, fontFamily: T.fontSans, fontWeight: 600,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              opacity: exportLoading ? 0.6 : 1, borderRadius: 12,
            }}
          >
            {exportLoading === "csv" ? <><Spinner size={11}/> Exporting...</> : <><DownloadSimple size={14} weight="bold"/> CSV</>}
          </button>
        </div>
        {exportErr && <p style={{ color: T.red || T.danger || "#e24b4a", fontSize: 11, fontFamily: T.fontSans, fontWeight: 600, margin: 0 }}>{exportErr}</p>}
      </div>

      {/* Send Feedback */}
      <div style={{ fontSize: 10, fontWeight: 700, color: T.inkMuted, letterSpacing: "1px", margin: "12px 0 6px" }}>Feedback</div>
      <button
        onClick={() => { try { navigate("/feedback"); } catch { window.location.href = "/feedback"; } }}
        style={{
          width: "100%", background: T.card, borderRadius: T.radius,
          border: `1px solid ${T.cardBorder}`, padding: "14px 16px", marginBottom: 8,
          display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left",
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: T.radiusSm, flexShrink: 0,
          background: T.surfaceLight, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <ChatCentered size={18} color={T.primary} weight="fill" />
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 13, fontFamily: T.fontSans, fontWeight: 600, color: T.inkDark }}>Send feedback</p>
          <p style={{ margin: "2px 0 0", fontSize: 11, fontFamily: T.fontSans, color: T.inkMuted }}>Report a bug or suggest a feature</p>
        </div>
        <CaretRight size={16} color={T.inkMuted} />
      </button>

      {/* Danger Zone */}
      <div style={{ fontSize: 10, fontWeight: 700, color: T.red || T.danger || "#e24b4a", letterSpacing: "1px", margin: "12px 0 6px" }}>Danger zone</div>
      <div style={{ background: T.card, borderRadius: 18, border: `1px solid ${T.red || T.danger || "#e24b4a"}`, padding: 16, marginBottom: 8 }}>
        <p style={{ fontFamily: T.fontDisplay, fontSize: 16, fontWeight: 800, color: T.red || T.danger || "#e24b4a", margin: "0 0 6px" }}>Delete account</p>
        <p style={{ color: T.inkMuted, fontSize: 11, fontFamily: T.fontSans, margin: "0 0 12px", lineHeight: 1.5 }}>
          Permanently delete your account and all data. This cannot be undone.
        </p>
        <button
          onClick={() => setShowDeleteModal(true)}
          style={{
            background: T.red || T.danger || "#e24b4a", border: "none", borderRadius: 12, padding: "10px 16px",
            cursor: "pointer", color: "#fff",
            fontSize: 12, fontFamily: T.fontSans, fontWeight: 600,
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          <Trash size={13} weight="bold"/> Delete account
        </button>
      </div>

      {/* Policy links */}
      <div style={{ textAlign: "center", padding: "12px 0 4px" }}>
        <span
          onClick={() => window.open("https://lithe.fit/privacy", "_blank")}
          style={{ fontSize: 12, fontFamily: T.fontSans, color: T.inkMuted, cursor: "pointer", textDecoration: "underline" }}
        >
          Privacy Policy
        </span>
        <span style={{ fontSize: 12, fontFamily: T.fontSans, color: T.inkMuted, margin: "0 8px" }}>·</span>
        <span
          onClick={() => window.open("https://lithe.fit/terms", "_blank")}
          style={{ fontSize: 12, fontFamily: T.fontSans, color: T.inkMuted, cursor: "pointer", textDecoration: "underline" }}
        >
          Terms of Service
        </span>
      </div>

      {/* App version */}
      <p style={{ textAlign: "center", margin: "8px 0 0", fontSize: 11, fontFamily: T.fontSans, color: T.inkMuted }}>
        Lithe.Fit v2.0.0
      </p>

      {/* Delete modal */}
      {showDeleteModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, padding: "20px" }}>
          <div style={{ background: T.card, borderRadius: 24, border: `1px solid ${T.cardBorder}`, width: "100%", maxWidth: 400, overflow: "hidden" }}>
            <div style={{ background: T.tileBg, padding: "16px 20px" }}>
              <p style={{ color: "#fff", fontSize: 16, fontFamily: T.fontDisplay, fontWeight: 800, margin: 0 }}>Delete account</p>
            </div>
            <div style={{ padding: 20 }}>
              <p style={{ color: T.inkDark, fontSize: 12, fontFamily: T.fontSans, margin: "0 0 16px", lineHeight: 1.6 }}>
                This will permanently delete your account, all food logs, weight logs, supplement data, and targets. This cannot be undone.
              </p>
              <p style={{ color: T.inkMuted, fontSize: 11, fontFamily: T.fontSans, fontWeight: 600, margin: "0 0 6px" }}>Type DELETE to confirm:</p>
              <input
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                style={{
                  width: "100%", background: T.surfaceLight, border: `1px solid ${T.red || T.danger || "#e24b4a"}`, borderRadius: 12, padding: "11px 14px",
                  color: T.inkDark, fontSize: 14, fontFamily: T.fontSans,
                  outline: "none", boxSizing: "border-box", marginBottom: 14,
                }}
              />
              {deleteErr && <p style={{ color: T.red || T.danger || "#e24b4a", fontSize: 11, fontFamily: T.fontSans, fontWeight: 600, margin: "0 0 10px" }}>{deleteErr}</p>}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(""); setDeleteErr(""); }}
                  style={{
                    flex: 1, background: T.surfaceLight, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: "10px",
                    cursor: "pointer", color: T.inkDark,
                    fontSize: 12, fontFamily: T.fontSans, fontWeight: 600,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== "DELETE" || deleteLoading}
                  style={{
                    flex: 1, borderRadius: 12,
                    background: deleteConfirmText === "DELETE" ? (T.red || T.danger || "#e24b4a") : T.surfaceLight,
                    border: `1px solid ${T.red || T.danger || "#e24b4a"}`, padding: "10px",
                    cursor: deleteConfirmText === "DELETE" ? "pointer" : "not-allowed",
                    color: deleteConfirmText === "DELETE" ? "#fff" : (T.red || T.danger || "#e24b4a"),
                    fontSize: 12, fontFamily: T.fontSans, fontWeight: 600,
                    opacity: deleteConfirmText === "DELETE" ? 1 : 0.5,
                  }}
                >
                  {deleteLoading ? "Deleting..." : "Delete forever"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── Tab 3: NOTIFICATIONS ─────────────────────────────────────────────────────
  const tabNotifications = (
    <div style={{ padding: "16px 16px 48px" }}>
      <div style={{background:T.card,borderRadius:18,border:`1px solid ${T.cardBorder}`,padding:16,overflow:"hidden"}}>
        <span style={{fontSize:10,fontWeight:700,color:T.inkMuted,letterSpacing:"1px",display:"block",marginBottom:10}}>In-app nudges</span>
        <p style={{ color: T.textDim, fontSize: 9, fontFamily: T.fontSans, fontWeight: "bold", margin: "0 0 4px", lineHeight: 1.5 }}>
          Control which smart prompts appear when you open the app.
        </p>
        <NudgeToggle
          id="supplement"
          label="Supplement Reminders"
          description="Prompts you to take supplements when a scheduled timing window opens."
        />
        <NudgeToggle
          id="hydration"
          label="Hydration Alert"
          description="Notifies you at 15:00 if you're below 50% of your water target."
        />
        <NudgeToggle
          id="log"
          label="Daily Log Reminder"
          description="Reminds you to log food after midday if you haven't logged anything today."
        />
        <NudgeToggle
          id="weight"
          label="Weekly Weigh-In"
          description="Nudges you to log your weight on Mondays when it's been 7+ days since your last weigh-in."
        />
        <NudgeToggle
          id="briefing"
          label="Weekly Briefing"
          description="Prompts you to run your weekly briefing on Sundays if you haven't done one this week."
        />
        <NudgeToggle
          id="workout"
          label="Workout Reminder"
          description="Suggests logging a workout if you haven't recorded one this week (shown on weekdays)."
        />
      </div>
      <p style={{ color: T.textDim, fontSize: 9, fontFamily: T.fontSans, fontWeight: "bold", margin: "4px 0 0", textAlign: "center", lineHeight: 1.5 }}>
        Changes take effect immediately. Nudges respect a once-per-day or once-per-week limit regardless of this setting.
      </p>
    </div>
  );

  // ── Health Connect settings opener ───────────────────────────────────────────
  const openHealthConnectSettings = () => {
    if (window?.Capacitor?.Plugins?.HealthConnect) {
      window.Capacitor.Plugins.HealthConnect.openHealthConnectSetting()
        .catch(() => {
          // fallback: silently fail — user can open Health Connect manually
        });
    }
  };

  // ── Tab 4: CONNECTED ─────────────────────────────────────────────────────────
  const tabConnected = (
    <div style={{ padding: "16px 16px 48px" }}>
      <span style={{fontSize:10,fontWeight:700,color:T.inkMuted,letterSpacing:"1px",display:"block",margin:"0 0 10px"}}>Connected apps</span>
      <div style={{background:T.card,borderRadius:18,border:`1px solid ${T.cardBorder}`,padding:16,overflow:"hidden"}}>
        {/* Header row */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
          <span style={{ fontSize:20 }}>🏃</span>
          <div>
            <p style={{ color:T.ink, fontSize:14, fontWeight:700, fontFamily:T.fontSans, margin:0 }}>
              Health Connect
            </p>
            <p style={{ color:T.textDim, fontSize:11, fontFamily:T.fontSans, margin:"2px 0 0", lineHeight:1.4 }}>
              Sync health and fitness data with Google Health Connect
            </p>
          </div>
        </div>

        {/* Reads section */}
        <span style={{fontSize:10,fontWeight:700,color:T.inkMuted,letterSpacing:"1px",display:"block",marginBottom:6}}>Reads from Health Connect</span>
        {[
          { label:"Steps",                  color:T.primary  },
          { label:"Active calories burned", color:"#F5A623"  },
          { label:"Heart rate",             color:"#E74C3C"  },
          { label:"Weight",                 color:T.primary  },
          { label:"Height",                 color:T.primary  },
        ].map(item => (
          <div key={item.label} style={{ display:"flex", alignItems:"center", gap:8, padding:"3px 0" }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background:item.color, flexShrink:0 }}/>
            <span style={{ color:T.textDim, fontSize:12, fontFamily:T.fontSans }}>{item.label}</span>
          </div>
        ))}

        {/* Connect / manage button */}
        <div style={{ marginTop:14 }}>
          {healthConnectAvailable && healthConnectGranted ? (
            <button onClick={openHealthConnectSettings} style={{
              width:"100%", padding:14, borderRadius:12,
              background:T.card, border:`1.5px solid ${T.primary}`,
              color:T.primary, fontSize:14, fontWeight:700,
              fontFamily:T.fontSans, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center", gap:8,
            }}>
              ✓ Connected — Tap to manage permissions
            </button>
          ) : (
            <button onClick={async () => {
              setHcNativeErr(false); setHcWebMsg(false); setHcSuccess(false);
              if (!window?.Capacitor?.isNativePlatform?.()) {
                setHcWebMsg(true);
                return;
              }
              const granted = await onConnectHealthConnect();
              if (granted) {
                setHcSuccess(true);
              } else {
                setHcNativeErr(true);
              }
            }} style={{
              width:"100%", padding:14, borderRadius:12,
              background:T.primary, border:"none",
              color:"#fff", fontSize:14, fontWeight:700,
              fontFamily:T.fontSans, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center", gap:8,
            }}>
              Connect Health Connect
            </button>
          )}
          {hcSuccess && (
            <div style={{background:"#ecfdf514",border:`1px solid #86efac`,borderRadius:T.radiusSm,padding:"10px 12px",marginTop:8,fontSize:11,fontFamily:T.fontSans,color:T.ink,fontWeight:600,lineHeight:1.5}}>
              Health Connect linked successfully. Your health data will sync automatically.
            </div>
          )}
          {hcWebMsg && (
            <div style={{background:`${T.primary}14`,border:`1px solid ${T.primary}33`,borderRadius:T.radiusSm,padding:"10px 12px",marginTop:8,fontSize:11,fontFamily:T.fontSans,color:T.ink,fontWeight:600,lineHeight:1.5}}>
              Health Connect is only available on Android devices with the Health Connect app installed. This feature won't work in the web browser.
            </div>
          )}
          {hcNativeErr && (
            <div style={{background:"#fef2f214",border:`1px solid #fecaca`,borderRadius:T.radiusSm,padding:"10px 12px",marginTop:8,fontSize:11,fontFamily:T.fontSans,color:T.ink,fontWeight:600,lineHeight:1.5}}>
              No permissions were granted. Please tap again and allow access when prompted.
            </div>
          )}
        </div>

        {/* Bottom note */}
        <p style={{ color:T.textDim, fontSize:10, fontFamily:T.fontSans, textAlign:"center", marginTop:10, lineHeight:1.5 }}>
          Health Connect must be installed on your device. Permissions can be managed in the Health Connect app at any time.
        </p>
      </div>
    </div>
  );

  const tabContent = [tabSettings, tabProfile, tabAccount, tabNotifications, tabConnected];

  return (
    <>{protocolModal}
    <div style={shell}><div style={phone}>
      {OfflineBanner}

      {/* Header */}
      <AppHeader
        title="Settings"
        subtitle="Account & preferences"
        persona={persona}
        darkMode={darkMode}
        onToggleDarkMode={()=>setDarkMode(d=>!d)}
        currentApp="nutri"
        onAvatarPress={()=>setActiveTab&&setActiveTab(1)}
        username={session?.user?.email}
        rightContent={<SyncBadge syncing={syncing} error={syncErr}/>}
      />

      {/* Tab bar */}
      <TabBar active={activeTab} setActive={setActiveTab}/>

      {/* Tab content */}
      <div style={{ overflowY: "auto", flex: 1 }}>
        {tabContent[activeTab]}
      </div>

      {hasTargets && <BottomNav screen={screen} setScreen={setScreen} hasTargets={hasTargets}/>}
    </div></div>
    </>
  );
}
